'use client';
import React, { useState, useEffect } from 'react';
import { entryService, userService } from '@/lib/supabaseService';
import { type ClimbEntry, type UserProfile } from '@/lib/climbData';
import Icon from '@/components/ui/AppIcon';
import AdminUsersTab from './AdminUsersTab';
import AdminEntriesTab from './AdminEntriesTab';
import AdminClimbsTab from './AdminClimbsTab';
import AdminSettingsTab from './AdminSettingsTab';
import { Toaster } from 'sonner';

type AdminTab = 'entries' | 'users' | 'climbs' | 'settings';

const TABS: { id: AdminTab; label: string; icon: string; badge?: number }[] = [
  { id: 'entries', label: 'Tempi da verificare', icon: 'ClockIcon' },
  { id: 'users', label: 'Gestione Utenti', icon: 'UsersIcon' },
  { id: 'climbs', label: 'Salite', icon: 'ArrowTrendingUpIcon' },
  { id: 'settings', label: 'Impostazioni', icon: 'Cog6ToothIcon' },
];

export default function AdminPanelPage() {
  const [activeTab, setActiveTab] = useState<AdminTab>('entries');
  const [entries, setEntries] = useState<ClimbEntry[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const [e, u] = await Promise.all([
          entryService.getAllForAdmin(),
          userService.getAll(),
        ]);
        setEntries(e);
        setUsers(u);
      } catch {
        // fallback to empty
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const pendingEntries = entries.filter((e) => e.status === 'pending').length;
  const pendingUsers = users.filter((u) => u.status === 'pending').length;
  const activeUsers = users.filter((u) => u.status === 'active').length;
  const totalApproved = entries.filter((e) => e.status === 'approved').length;

  const tabsWithBadges = TABS.map((t) => ({
    ...t,
    badge: t.id === 'entries' ? pendingEntries : t.id === 'users' ? pendingUsers : undefined,
  }));

  return (
    <div className="py-8">
      <Toaster position="bottom-right" theme="dark" richColors />

      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Icon name="Cog6ToothIcon" size={22} className="text-primary" />
            <h1 className="text-3xl font-extrabold text-foreground">
              Admin <span className="text-gradient-orange">Panel</span>
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            Gestione club IA Climb · Stagione 2026
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-muted border border-border">
            <span className="w-2 h-2 rounded-full bg-primary" />
            <span className="text-xs text-muted-foreground font-semibold">Admin</span>
            <span className="text-xs text-foreground font-bold">Admin IA Climb</span>
          </div>
        </div>
      </div>

      {/* Admin KPI strip */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          {[
            { id: 'akpi-pending-entries', label: 'Tempi in attesa', value: pendingEntries.toString(), icon: 'ClockIcon', color: pendingEntries > 0 ? 'text-yellow-400' : 'text-green-400', bg: pendingEntries > 0 ? 'bg-yellow-400/10' : 'bg-green-400/10', alert: pendingEntries > 0 },
            { id: 'akpi-pending-users', label: 'Utenti da approvare', value: pendingUsers.toString(), icon: 'UserPlusIcon', color: pendingUsers > 0 ? 'text-yellow-400' : 'text-green-400', bg: pendingUsers > 0 ? 'bg-yellow-400/10' : 'bg-green-400/10', alert: pendingUsers > 0 },
            { id: 'akpi-active-users', label: 'Utenti attivi', value: activeUsers.toString(), icon: 'UsersIcon', color: 'text-green-400', bg: 'bg-green-400/10', alert: false },
            { id: 'akpi-approved', label: 'Tempi approvati', value: totalApproved.toString(), icon: 'CheckCircleIcon', color: 'text-primary', bg: 'bg-primary/10', alert: false },
          ].map((kpi) => (
            <div
              key={kpi.id}
              className={`bg-card border rounded-xl p-4 flex items-center gap-3 ${kpi.alert ? 'border-yellow-400/30 card-glow' : 'border-border'}`}
            >
              <div className={`${kpi.bg} rounded-lg p-2.5 flex-shrink-0`}>
                <Icon name={kpi.icon as 'ClockIcon'} size={20} className={kpi.color} />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">{kpi.label}</p>
                <p className={`text-2xl font-bold font-mono-nums ${kpi.color}`}>{kpi.value}</p>
              </div>
              {kpi.alert && kpi.value !== '0' && (
                <div className="ml-auto">
                  <Icon name="ExclamationTriangleIcon" size={16} className="text-yellow-400" />
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Tab nav */}
      <div className="flex gap-2 mb-6 border-b border-border pb-0 overflow-x-auto">
        {tabsWithBadges.map((tab) => (
          <button
            key={`admin-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-all duration-150 whitespace-nowrap -mb-px ${
              activeTab === tab.id
                ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
            }`}
          >
            <Icon name={tab.icon as 'ClockIcon'} size={16} />
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-yellow-400 text-yellow-900 text-xs font-bold">
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'entries' && <AdminEntriesTab />}
        {activeTab === 'users' && <AdminUsersTab />}
        {activeTab === 'climbs' && <AdminClimbsTab />}
        {activeTab === 'settings' && <AdminSettingsTab />}
      </div>
    </div>
  );
}